/**
 * Some supplier descriptions contain a stylesheet as plain text before the HTML.
 * Only consume complete, recognizable CSS rules at the start: never remove
 * arbitrary brace-delimited text from the actual product description.
 * Shared by browser preview, server import and the scheduled Edge importer.
 */
export function stripLeadingDescriptionCss(value: string): string {
  let cursor = 0;
  let consumed = 0;
  while (cursor < value.length) {
    const trivia = value.slice(cursor).match(/^(?:\s|\/\*[\s\S]*?\*\/)+/);
    if (trivia) cursor += trivia[0].length;
    const start = cursor;
    const open = value.indexOf('{', start);
    if (open < 0) break;
    const selector = value.slice(start, open).trim();
    // Restrict bare element selectors to HTML tags to avoid swallowing prose.
    const recognized = /^(?:[.#][\w-]|\*|\[|:|(?:html|body|div|span|p|h[1-6]|ul|ol|li|a|img|table|thead|tbody|tr|td|th|section|article)\b|@(?:media|supports|font-face|(?:-webkit-)?keyframes|layer|container)\b)/i;
    if (!recognized.test(selector) || /[<>;]/.test(selector)) break;

    let depth = 1;
    let quote = '';
    let comment = false;
    let end = open + 1;
    for (; end < value.length; end++) {
      const char = value[end];
      if (comment) {
        if (char === '*' && value[end + 1] === '/') { comment = false; end++; }
      } else if (quote) {
        if (char === '\\') end++;
        else if (char === quote) quote = '';
      } else if (char === '/' && value[end + 1] === '*') {
        comment = true;
        end++;
      } else if (char === '"' || char === "'") {
        quote = char;
      } else if (char === '<') {
        break; // Malformed CSS must not consume following HTML.
      } else if (char === '{') {
        depth++;
      } else if (char === '}' && --depth === 0) {
        break;
      }
    }
    if (depth !== 0) break;
    const body = value.slice(open + 1, end);
    if (body.trim() && !/(?:--[\w-]+|[a-z-]+)\s*:/i.test(body)) break;
    cursor = end + 1;
    consumed = cursor;
  }
  return consumed ? value.slice(consumed).trimStart() : value;
}
